import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, IntlShape } from 'react-intl';
import React from 'react';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IAlertsService } from '../../infrastructure/alerts';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { DeepReadonly } from '../../infrastructure/type-extensions';
import { IAccountDetailsDTO, IAccountsDAO } from '../../api/accounts.dao';
import { RADIX_PARAM } from '../../constants/common.constants';
import { IUserGroupDTO, IUserGroupsDAO, IUserGroupsListDTOInternal } from '../../api/user-groups.dao';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { UserGroupsForm } from './_user-groups-form/user-groups-form';

class UserGroupsVM implements IModuleVM {
    @mobx.observable
    public userGroupsList: IUserGroupDTO[];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public groupIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    private subscriptions: Unsubscribe[];
    @mobx.observable
    public loadMorePaginationToken: string | null;
    @mobx.observable
    private account: IAccountDetailsDTO;
    @mobx.observable
    private loadingAccount: boolean;

    @mobx.observable
    public paginationVM: PagingViewModel;

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly usersGroupsDAO: IUserGroupsDAO = IoC.Container.get(IUserGroupsDAO)
    ) {
        this.groupIdToSubWindowMap = {};
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchUserGroupsList);
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(
            this.router.listen(Routes.UserGroupsList, (params: { accountId: string }) => {
                mobx.runInAction(() => {
                    this.isLoading = true;

                    this.fetchAccount(params.accountId).then(() => {
                        this.fetchUserGroupsList();
                    });
                });
            })
        );
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchUserGroupsList = async () => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });

        try {
            const params = {
                accountId: this.account.accountId,
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize()
            };

            const response = await this.usersGroupsDAO.getUserGroups(params);
            this.onFetchUserGroupsListSuccess(response);
        } catch (error) {
            this.onFetchUserGroupsListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchUserGroupsListSuccess(response: IUserGroupsListDTOInternal): void {
        this.userGroupsList = response.content;

        this.paginationVM.setTotalRecords(response.totalElements);
    }

    @mobx.action
    private onFetchUserGroupsListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    public showForm = params => {
        const { title, userGroupId } = params;
        return this.subWindowService.showSubWindow({
            componentToHost: UserGroupsForm,
            componentToHostProps: {
                fetchUserGroupsList: this.fetchUserGroupsList,
                closeEditGroupForm: this.closeSubWindow,
                userGroupId: userGroupId,
                accountId: this.account.accountId
            },
            title: title
        });
    };

    public editGroup = (userGroupId: number) => {
        let shallShowNewEditWindow = true;
        const alreadyShownSubWindow = this.groupIdToSubWindowMap[userGroupId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            mobx.runInAction(() => {
                this.groupIdToSubWindowMap[userGroupId] = this.showForm({
                    title: (
                        <FormattedMessage
                            id='oe.userGroups.editUserGroup'
                            description='Edit User Group button'
                            defaultMessage='Edit User Group'
                        />
                    ),
                    userGroupId: userGroupId
                });
            });
        }
    };

    public showCreateGroupForm = (intl: IntlShape) => {
        return this.subWindowService.showSubWindow({
            componentToHost: UserGroupsForm,
            componentToHostProps: {
                fetchUserGroupsList: this.fetchUserGroupsList,
                closeEditGroupForm: this.closeSubWindow,
                accountId: this.account.accountId
            },
            title: intl.formatMessage({
                id: `oe.userGroups.newUserGroup`,
                description: 'New User Group button',
                defaultMessage: 'New User Group'
            })
        });
    };

    public isGroupFormShown = (userGroupId: number) => {
        return (
            this.groupIdToSubWindowMap[userGroupId] &&
            this.groupIdToSubWindowMap[userGroupId].getStatus() !== SubWindowStatus.Closed
        );
    };

    public closeSubWindow = (userGroupId: number) => {
        this.groupIdToSubWindowMap[userGroupId.toString()].close();
    };

    @mobx.action
    private async fetchAccount(accountIdString: string) {
        const accountId = parseInt(accountIdString, RADIX_PARAM);

        if (!this.account || accountId !== this.account.accountId) {
            this.loadingAccount = true;
            let account;

            try {
                account = await this.accountsDAO.readAccount(accountId);
            } catch (error) {
                this.router.navigate(Routes.AccountsList, {});
            }

            if (account) this.setAccount(account);
        }
    }

    @mobx.action
    private setAccount(account: IAccountDetailsDTO) {
        this.loadingAccount = false;
        this.account = account;
    }
    public getAccount() {
        return this.account as DeepReadonly<IAccountDetailsDTO>;
    }
    public isLoadingAccount() {
        return this.loadingAccount;
    }
}

export { UserGroupsVM };
